import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import { useAuth } from '@/lib/contexts/AuthContext'
import {
  getNotificationPreferences,
  updateNotificationPreferences,
} from '@/lib/api/api'
import { HugeiconsIcon } from '@hugeicons/react'
import { Cancel01Icon } from '@hugeicons/core-free-icons'
import {
  SettingsWell,
  SettingsCard,
  SettingsRow,
  SettingsToggle,
  settingsButtonClass,
} from '@/components/shared/settings/SettingsKit'

interface NotificationsTabProps {
  domain: string
  propertyId?: string
}

interface NotificationSettings {
  weeklyReport: { enabled: boolean; emails: string[] }
  monthlyReport: { enabled: boolean; emails: string[] }
  trafficSpike: { enabled: boolean; emails: string[] }
  trafficDrop: { enabled: boolean; emails: string[] }
  splitTestActivity: { enabled: boolean; emails: string[] }
}

const defaultSettings: NotificationSettings = {
  weeklyReport: { enabled: false, emails: [] },
  monthlyReport: { enabled: false, emails: [] },
  trafficSpike: { enabled: false, emails: [] },
  trafficDrop: { enabled: false, emails: [] },
  splitTestActivity: { enabled: false, emails: [] },
}

export default function NotificationsTab({ propertyId }: NotificationsTabProps) {
  const { token } = useAuth()

  const [settings, setSettings] = useState<NotificationSettings>(defaultSettings)
  const [newEmails, setNewEmails] = useState({
    weekly: '',
    monthly: '',
    spike: '',
    drop: '',
    splitTest: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadPreferences()
  }, [propertyId, token])

  const loadPreferences = async () => {
    if (!token || !propertyId) return

    try {
      setIsLoading(true)
      const data = await getNotificationPreferences(propertyId, token)
      if (data) {
        setSettings({
          weeklyReport: data.weeklyReport || defaultSettings.weeklyReport,
          monthlyReport: data.monthlyReport || defaultSettings.monthlyReport,
          trafficSpike: data.trafficSpike || defaultSettings.trafficSpike,
          trafficDrop: data.trafficDrop || defaultSettings.trafficDrop,
          splitTestActivity: data.splitTestActivity || defaultSettings.splitTestActivity,
        })
      }
    } catch (error) {
      console.error('Error loading notification preferences:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const validateEmail = (email: string): boolean => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    return re.test(email)
  }

  const saveSettings = async (newSettings: NotificationSettings) => {
    if (!token || !propertyId || isSubmitting) return

    setIsSubmitting(true)
    try {
      await updateNotificationPreferences(propertyId, newSettings, token)
      toast.success('Email reports saved')
    } catch (error) {
      console.error('Error saving notification settings:', error)
      toast.error("Couldn't save email reports")
    } finally {
      setIsSubmitting(false)
    }
  }

  const toggleSetting = (key: keyof NotificationSettings, enabled: boolean) => {
    const newSettings = {
      ...settings,
      [key]: { ...settings[key], enabled },
    }
    setSettings(newSettings)
    saveSettings(newSettings)
  }

  const addEmail = (
    key: keyof NotificationSettings,
    emailKey: keyof typeof newEmails,
  ) => {
    const email = newEmails[emailKey].trim()

    if (!email) {
      toast.error('Enter an email address')
      return
    }

    if (!validateEmail(email)) {
      toast.error("That doesn't look like an email address")
      return
    }

    if (settings[key].emails.includes(email)) {
      toast.error('That email already gets this report')
      return
    }

    const newSettings = {
      ...settings,
      [key]: {
        ...settings[key],
        emails: [...settings[key].emails, email],
      },
    }
    setSettings(newSettings)
    setNewEmails({ ...newEmails, [emailKey]: '' })
    saveSettings(newSettings)
  }

  const removeEmail = (key: keyof NotificationSettings, index: number) => {
    const newSettings = {
      ...settings,
      [key]: {
        ...settings[key],
        emails: settings[key].emails.filter((_, i) => i !== index),
      },
    }
    setSettings(newSettings)
    saveSettings(newSettings)
  }

  const renderRow = (
    title: string,
    description: string,
    settingKey: keyof NotificationSettings,
    emailKey: keyof typeof newEmails,
  ) => (
    <SettingsToggle
      key={settingKey}
      title={title}
      description={description}
      checked={settings[settingKey].enabled}
      onCheckedChange={(checked) => toggleSetting(settingKey, checked)}
    >
      <div className="flex gap-2">
        <Input
          type="email"
          aria-label={`Add a recipient for ${title.toLowerCase()}`}
          placeholder="Add recipient email"
          value={newEmails[emailKey]}
          onChange={(e) => setNewEmails({ ...newEmails, [emailKey]: e.target.value })}
          className="h-8 flex-1 rounded-lg px-2.5 text-[13px]"
          onKeyDown={(e) => {
            if (e.key === 'Enter') addEmail(settingKey, emailKey)
          }}
        />
        <Button
          variant="outline"
          className={`${settingsButtonClass} h-8 px-2.5`}
          onClick={() => addEmail(settingKey, emailKey)}
          disabled={isSubmitting || !newEmails[emailKey].trim()}
        >
          Add
        </Button>
      </div>
      {settings[settingKey].emails.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {settings[settingKey].emails.map((email, index) => (
            <li
              key={email}
              className="flex h-6 items-center gap-1 rounded-md bg-card pl-2 pr-1 text-xs leading-4 text-foreground ring-1 ring-inset ring-border"
            >
              <span className="truncate">{email}</span>
              <button
                type="button"
                onClick={() => removeEmail(settingKey, index)}
                disabled={isSubmitting}
                aria-label={`Remove ${email}`}
                className="inline-flex size-4 items-center justify-center rounded text-foreground/40 transition-colors hover:text-foreground"
              >
                <HugeiconsIcon icon={Cancel01Icon} size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </SettingsToggle>
  )

  if (isLoading) {
    return (
      <SettingsWell>
        <SettingsCard>
          <div className="flex flex-col gap-2 py-6">
            <div className="h-14 animate-pulse rounded-lg bg-muted/60" />
            <div className="h-14 animate-pulse rounded-lg bg-muted/40" />
          </div>
        </SettingsCard>
      </SettingsWell>
    )
  }

  return (
    <SettingsWell>
      <SettingsCard>
        <SettingsRow title="Scheduled reports" description="Digests with traffic, tests and conversion highlights.">
          <div className="flex flex-col gap-2">
            {renderRow('Weekly report', 'Every Monday morning', 'weeklyReport', 'weekly')}
            {renderRow('Monthly report', '1st of each month', 'monthlyReport', 'monthly')}
          </div>
        </SettingsRow>
        <SettingsRow title="Alerts" description="An email when traffic or tests move outside the normal range.">
          <div className="flex flex-col gap-2">
            {renderRow('Traffic spike', 'Unusually high traffic', 'trafficSpike', 'spike')}
            {renderRow('Traffic drop', 'Unusually low over a 12-hour window', 'trafficDrop', 'drop')}
            {renderRow('Test with low activity', 'Running tests with little traffic or few conversions', 'splitTestActivity', 'splitTest')}
          </div>
        </SettingsRow>
      </SettingsCard>
    </SettingsWell>
  )
}
